import React, { useRef, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import AsyncSelect from 'react-select/async';
import { Link } from "react-router-dom";
import { hasValidPhone, getPhoneWithCountryCode } from "../utils/phoneUtils";

import EditProfileForm from '../../../Home/Forms/EditProfileForm';
import ChangePasswordForm from '../../../Home/Forms/ChangePasswordForm';
import ChangeLoginInfoForm from "../../../Home/Forms/ChangeLoginInfoForm";
import UserBill from "../UserBill";
import UserNote from "../UserNote";
import configCountries from "../../../Config/countries.json";
import { LazyLoadImage } from "react-lazy-load-image-component";
import api from "../../../../api/api";

const formatDate = (dateVal) => {
    if (!dateVal) return null;
    if (typeof dateVal === "string") {
        return dateVal.split("T")[0];
    }
    return dateVal;
};

const UserModals = ({
    activeModal,
    activeUser,
    onClose,
    onSubmitEdit,
    onSubmitPassword,
    onSubmitLoginInfo,
    onSubmitChangeSchool,
    onSubmitChangeClassRoom,
    promiseSchoolOptions,
    promiseClassRoomOptions,
    onAction,
    t
}) => {
    const schoolsSelectRef = useRef(null);
    const classRoomSelectRef = useRef(null);

    if (!activeModal || !activeUser) return null;

    const selectStyle = {
        theme: theme => ({
            ...theme,
            borderRadius: 0,
            colors: {
                ...theme.colors,
                primary25: '#92278f',
                primary: '#92278f',
            },
        })
    };

    const handleSaveSchool = () => {
        const selected = schoolsSelectRef.current?.select?.select?.getValue();
        const value = selected && selected[0]?.value;
        const label = selected && selected[0]?.label;
        onSubmitChangeSchool(value, label);
    };

    const handleSaveClassRoom = () => {
        const selected = classRoomSelectRef.current?.select?.select?.getValue();
        const value = selected && selected[0]?.value;
        onSubmitChangeClassRoom(value);
    };

    return (
        <>
            {/* Bills Modal */}
            {activeModal === "bills" && (
                <UserBill handleClose={onClose} user={activeUser} />
            )}

            {/* Notes Modal */}
            {activeModal === "notes" && (
                <UserNote handleClose={onClose} user={activeUser} />
            )}

            {/* Change Login Info Modal */}
            {activeModal === "changeLoginInfo" && (
                <Modal size="md" centered backdrop="static" keyboard={false} show={true} onHide={onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="h5 mb-0">
                                <i className="fad fa-fingerprint text-success icon ml-2"></i>
                                {t("Pages.UsersControl.action.changeLoginInfo")}:
                                <span className="text-primary mr-1">{activeUser.Name}</span>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <ChangeLoginInfoForm onSubmit={onSubmitLoginInfo} user={activeUser} />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {/* Change Password Modal */}
            {activeModal === "change_password" && (
                <Modal size="md" centered backdrop="static" keyboard={false} show={true} onHide={onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="h5 mb-0">
                                <i className="fad fa-key text-primary icon ml-2"></i>
                                {t("Pages.UsersControl.action.change_password")}:
                                <span className="text-primary mr-1">{activeUser.Name}</span>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <ChangePasswordForm onSubmit={onSubmitPassword} />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {/* Edit Profile Modal */}
            {activeModal === "edit" && (
                <Modal size="lg" centered backdrop="static" keyboard={false} show={true} onHide={onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="h5 mb-0">
                                <i className="fad fa-edit text-primary icon ml-2"></i>
                                {t("Pages.UsersControl.action.edit")}:
                                <span className="text-primary mr-1">{activeUser.Name}</span>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <EditProfileForm isAdmin={true} user={activeUser} onSubmit={onSubmitEdit} />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {/* Change School Modal */}
            {activeModal === "changeSchool" && (
                <Modal size="md" centered backdrop="static" keyboard={false} show={true} onHide={onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="h5 mb-0">
                                <i className="fad fa-school text-primary icon ml-2"></i>
                                {activeUser.UserType?.Id === 2 ? (
                                    t("Pages.UsersControl.action.changeTypeToSchoolTeacher")
                                ) : (
                                    <>
                                        {t("Pages.UsersControl.action.changeSchool")}:
                                        <span className="text-primary mr-1">{activeUser.Name}</span>
                                    </>
                                )}
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category mb-2">
                            <div className="widget-body">
                                <div className="form-group w-100">
                                    <AsyncSelect
                                        cacheOptions
                                        isClearable={true}
                                        name="SchoolsId"
                                        placeholder={t('Pages.UsersControl.advancedSearch.School')}
                                        isRtl={true}
                                        isSearchable={true}
                                        defaultOptions
                                        loadOptions={promiseSchoolOptions}
                                        ref={schoolsSelectRef}
                                        {...selectStyle}
                                    />
                                </div>
                                <div className="form-group w-100 text-center">
                                    <button className="custom-button btn-block" onClick={handleSaveSchool}>
                                        <span>
                                            <i className="icon fal fa-save ml-1"></i>
                                            {t('Pages.UsersControl.notes.Send')}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {/* Move Student Class Room Modal */}
            {activeModal === "move" && (
                <Modal size="md" centered backdrop="static" keyboard={false} show={true} onHide={onClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="h5 mb-0">
                                <i className="fad fa-exchange text-primary icon ml-2"></i>
                                {t("Pages.StudentInClassRoomsControl.action.move")}
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category mb-2">
                            <div className="widget-body">
                                <div className="form-group w-100">
                                    <AsyncSelect
                                        cacheOptions
                                        isClearable={true}
                                        name="ClassRoomId"
                                        placeholder={t('Pages.ClassRoomsControl.title')}
                                        isRtl={true}
                                        isSearchable={true}
                                        defaultOptions
                                        loadOptions={promiseClassRoomOptions}
                                        ref={classRoomSelectRef}
                                        {...selectStyle}
                                    />
                                </div>
                                <div className="form-group w-100 text-center">
                                    <button className="custom-button btn-block" onClick={handleSaveClassRoom}>
                                        <span>
                                            <i className="icon fal fa-exchange ml-1"></i>
                                            {t('Pages.StudentInClassRoomsControl.action.move_action')}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {/* More Info Modal */}
            {activeModal === "moreInfo" && (
                <Modal size="lg" centered show={true} onHide={onClose} className="more-info-modal-container">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="d-flex align-items-center">
                                <div className="more-info-icon-wrapper ml-3">
                                    <i className="fad fa-user-chart"></i>
                                </div>
                                <div className="text-right">
                                    <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.action.info")}</div>
                                    <div className="small text-muted">{activeUser.Name}</div>
                                </div>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3 pb-4">
                        {(() => {
                            const countryObj = activeUser.Country
                                ? configCountries.countries.find(
                                      (c) => c.value.toLowerCase() === activeUser.Country.toLowerCase()
                                  )
                                : null;

                            const phoneWithCode = getPhoneWithCountryCode(activeUser.Phone, activeUser.Country);
                            const hasPhone = hasValidPhone(activeUser.Phone);

                            const userTypeId = activeUser.UserType ? activeUser.UserType.Id : null;
                            const isTeacher = userTypeId === 2 || userTypeId === 4;
                            const isStudent = userTypeId === 5;
                            const isAdmin = userTypeId === 1;

                            let roleBadgeClass = "badge-secondary";
                            if (isAdmin) roleBadgeClass = "badge-danger";
                            else if (isTeacher) roleBadgeClass = "badge-info";
                            else if (isStudent) roleBadgeClass = "badge-primary";

                            const handleActionClick = (action) => {
                                onAction(action, activeUser);
                                if (["useAs", "remove", "restore", "fire", "testUser", "changePermission", "accessToAllClass", "changeUserType"].includes(action)) {
                                    onClose();
                                }
                            };

                            return (
                                <div className="more-info-layout-wrapper">
                                    {/* Main Panel: User Profile Hero & Card Grid */}
                                    <div className="more-info-main-panel">
                                        
                                        {/* Profile Hero Header */}
                                        <div className="more-info-hero">
                                            <div className="hero-avatar-section">
                                                <div className="hero-avatar-wrapper position-relative">
                                                    <LazyLoadImage
                                                        src={activeUser.Photo || "/assets/images/logo/130p150.png"}
                                                        alt={activeUser.Name}
                                                        className={`hero-avatar-img ${activeUser.IsActive ? "border-active" : "border-inactive"}`}
                                                        title={activeUser.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive")}
                                                    />
                                                    {activeUser.ActivateUntilDate && activeUser.ActivateUntilDate !== "0" && (
                                                        <span className="hero-crown-badge" title={t("Pages.Profile.ActivateUntilDate.title") + ": " + activeUser.ActivateUntilDate}>
                                                            <i className="fa-solid fa-crown text-warning"></i>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="hero-details-section">
                                                <div className="d-flex align-items-center flex-wrap gap-2 justify-content-start">
                                                    <h4 className="hero-name mb-0 font-weight-bold text-dark">{activeUser.Name}</h4>
                                                    {activeUser.Sex && (
                                                        <i 
                                                            className={`fas fa-${activeUser.Sex === "female" ? "venus text-danger" : "mars text-primary"} hero-sex-icon mr-2`}
                                                            title={t('Pages.Register.Sex.' + activeUser.Sex)}
                                                            style={{ fontSize: '1rem' }}
                                                        ></i>
                                                    )}
                                                </div>
                                                <div className="hero-meta text-muted mt-2 d-flex align-items-center flex-wrap gap-2">
                                                    <span className="hero-username">@{activeUser.Username}</span>
                                                    <span className="hero-divider text-muted mx-1">•</span>
                                                    <span className={`badge ${roleBadgeClass} hero-role-badge px-2 py-1`}>
                                                        {activeUser.UserType?.Name || t("NoInfo")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Grid Cards */}
                                        <div className="more-info-grid-modern mt-4">
                                            {/* Role / User Type */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-info">
                                                    <i className="fad fa-user-shield"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.WhatsApp.userType")}</span>
                                                    <span className="info-detail-value-modern">
                                                        {activeUser.UserType?.Name || t("NoInfo")}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* School */}
                                            {activeUser.School && userTypeId !== 1 && userTypeId !== 6 && (
                                                <div className="info-detail-card-modern">
                                                    <div className="info-detail-icon-modern text-purple" style={{ color: 'var(--brand-purple)' }}>
                                                        <i className="fad fa-school"></i>
                                                    </div>
                                                    <div className="info-detail-content-modern">
                                                        <span className="info-detail-label-modern">{t("Pages.WhatsApp.school")}</span>
                                                        <span className="info-detail-value-modern">
                                                            {activeUser.School?.Name || t("NoInfo")}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Class Room (For Students) */}
                                            {userTypeId === 5 && (
                                                <div className="info-detail-card-modern">
                                                    <div className="info-detail-icon-modern text-primary" style={{ color: '#0ea5e9' }}>
                                                        <i className="fad fa-chalkboard-teacher"></i>
                                                    </div>
                                                    <div className="info-detail-content-modern">
                                                        <span className="info-detail-label-modern">{t("Pages.Profile.ClassRoom.title") || "الصف الدراسي"}</span>
                                                        <span className="info-detail-value-modern">
                                                            {activeUser.ClassRoom || t("NoInfo")}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Email */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-primary">
                                                    <i className="fad fa-envelope-open-text"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Register.Email.title")}</span>
                                                    <span className="info-detail-value-modern">
                                                        {activeUser.Email ? (
                                                            <a href={`mailto:${activeUser.Email}`} className="text-decoration-none text-dark hover-primary-link">
                                                                {activeUser.Email}
                                                            </a>
                                                        ) : (
                                                            t("NoInfo")
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Phone */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-success">
                                                    <i className="fad fa-phone-alt"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Register.Phone.title")}</span>
                                                    <span className="info-detail-value-modern" dir="ltr" style={{ textAlign: 'right' }}>
                                                        {activeUser.Phone ? (
                                                            <a href={`tel:${activeUser.Phone}`} className="text-decoration-none text-dark hover-success-link">
                                                                {activeUser.Phone}
                                                            </a>
                                                        ) : (
                                                            t("NoInfo")
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Gender (Sex) */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-warning">
                                                    <i className={`fad fa-${activeUser.Sex === "female" ? "venus text-danger" : "mars text-primary"}`}></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Register.Sex.title")}</span>
                                                    <span className="info-detail-value-modern">
                                                        {activeUser.Sex ? (
                                                            <>
                                                                <i className={`fas fa-${activeUser.Sex === "female" ? "venus text-danger" : "mars text-primary"} ml-1`}></i>
                                                                {t('Pages.Register.Sex.' + activeUser.Sex)}
                                                            </>
                                                        ) : (
                                                            t("NoInfo")
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Birthday */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-danger">
                                                    <i className="fad fa-birthday-cake"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Register.Birthday.title")}</span>
                                                    <span className="info-detail-value-modern">
                                                        {formatDate(activeUser.Birthday) || t("NoInfo")}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Status / Activation */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-success">
                                                    <i className="fad fa-shield-check"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Profile.ActivateUntilDate.title")}</span>
                                                    <span className="info-detail-value-modern">
                                                        {activeUser.ActivateUntilDate !== "0" ? (
                                                            <span className="text-success font-weight-bold">
                                                                <i className="fas fa-calendar-alt ml-1"></i>
                                                                {activeUser.ActivateUntilDate}
                                                            </span>
                                                        ) : (
                                                            <span className="text-danger font-weight-bold">
                                                                {t('Pages.UsersControl.NotActive')}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Activation Code */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-warning">
                                                    <i className="fad fa-key"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Profile.ActivateCode.title") || "كود التفعيل"}</span>
                                                    <span className="info-detail-value-modern" style={{ fontWeight: '600', letterSpacing: '0.5px' }}>
                                                        {activeUser.ActivateCode || t("NoInfo")}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Last Login */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-primary">
                                                    <i className="fad fa-sign-in-alt"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Profile.LastLogin.title")}</span>
                                                    <span className="info-detail-value-modern">{activeUser.LastLogin || t("NoInfo")}</span>
                                                </div>
                                            </div>

                                            {/* Country */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-info">
                                                    <i className="fad fa-globe-asia"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.UsersControl.advancedSearch.Country.title")}</span>
                                                    <span className="info-detail-value-modern d-flex align-items-center gap-1 justify-content-start">
                                                        {countryObj ? (
                                                            <div className="large-flag-hover-wrapper" title={`${countryObj.label}${activeUser.Address ? ' - ' + activeUser.Address : ''}`}>
                                                                <LazyLoadImage
                                                                    src={process.env.PUBLIC_URL + countryObj.icon}
                                                                    alt={countryObj.label}
                                                                    className="user-country-flag-large ml-1"
                                                                />
                                                                <span>{countryObj.label}</span>
                                                            </div>
                                                        ) : (
                                                            activeUser.Country || t("NoInfo")
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Address */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-warning">
                                                    <i className="fad fa-map-marker-alt"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Register.Address.title") || "العنوان"}</span>
                                                    <span className="info-detail-value-modern">{activeUser.Address || t("NoInfo")}</span>
                                                </div>
                                            </div>

                                            {/* Creation Date */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-secondary">
                                                    <i className="fad fa-calendar-plus"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Lessons.CreationDate")}</span>
                                                    <span className="info-detail-value-modern">{activeUser.CreationDate || t("NoInfo")}</span>
                                                </div>
                                            </div>

                                            {/* Update Date */}
                                            <div className="info-detail-card-modern">
                                                <div className="info-detail-icon-modern text-secondary">
                                                    <i className="fad fa-calendar-edit"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.Lessons.UpdateDate")}</span>
                                                    <span className="info-detail-value-modern">{activeUser.UpdateDate || t("NoInfo")}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Flags */}
                                        {(activeUser.IsSuperTeacher || activeUser.IsForTest || activeUser.IsLocked || !activeUser.IsActive) && (
                                            <div className="more-info-flags-card-modern mt-3">
                                                <div className="info-detail-icon-modern text-muted">
                                                    <i className="fad fa-tags"></i>
                                                </div>
                                                <div className="info-detail-content-modern">
                                                    <span className="info-detail-label-modern">{t("Pages.UsersControl.advancedSearch.status")}</span>
                                                    <div className="d-flex flex-wrap gap-2 mt-1 justify-content-start">
                                                        {activeUser.IsSuperTeacher && (
                                                            <span className="badge badge-success p-2">
                                                                <i className="fas fa-award ml-1"></i>
                                                                {t("Pages.Register.IsSuperTeacher") || "صلاحيّات مركز"}
                                                            </span>
                                                        )}
                                                        {activeUser.IsForTest && (
                                                            <span className="badge badge-info p-2">
                                                                <i className="fas fa-vial ml-1"></i>
                                                                {t("Pages.UsersControl.IsForTest") || "الحساب تجريبي"}
                                                            </span>
                                                        )}
                                                        {activeUser.IsLocked && (
                                                            <span className="badge badge-dark p-2">
                                                                <i className="fas fa-lock ml-1"></i>
                                                                {t("Pages.UsersControl.Locked")}
                                                            </span>
                                                        )}
                                                        {!activeUser.IsActive && (
                                                            <span className="badge badge-danger p-2">
                                                                <i className="fas fa-user-slash ml-1"></i>
                                                                {t("Pages.UsersControl.NotActive")}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Sidebar: Administrative Actions Menu */}
                                    <div className="more-info-sidebar-panel">
                                        <div className="sidebar-section-header-modern">
                                            <i className="fad fa-sliders-h ml-2 text-primary"></i>
                                            <span>{t("Pages.UsersControl.actionsMenu") || "إجراءات التحكم والخيارات"}</span>
                                        </div>

                                        <div className="sidebar-actions-scroll scrollbar-custom">
                                            {activeUser.IsDeleted ? (
                                                <div className="sidebar-group-modern">
                                                    <div className="sidebar-group-title-modern text-success">{t("Pages.UsersControl.IsDeleted")}</div>
                                                    <button type="button" className="sidebar-action-btn-modern success-btn" onClick={() => handleActionClick("restore")}>
                                                        <i className="far fa-trash-undo-alt icon ml-2"></i>
                                                        <span>{t("Pages.UsersControl.action.undo")}</span>
                                                    </button>
                                                    <button type="button" className="sidebar-action-btn-modern danger-btn mt-2" onClick={() => handleActionClick("fire")}>
                                                        <i className="far fa-trash icon text-danger ml-2"></i>
                                                        <span>{t("Pages.UsersControl.action.remove")}</span>
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Primary Actions Group */}
                                                    <div className="sidebar-group-modern">
                                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.advancedSearch.personalSection") || "بيانات الحساب"}</div>
                                                        
                                                        {userTypeId !== 7 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("edit")}>
                                                                <i className="far fa-edit icon text-info ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.edit")}</span>
                                                            </button>
                                                        )}

                                                        {hasPhone && (
                                                            <Link 
                                                                to={`/ControlPanel/WhatsApp/${phoneWithCode}`}
                                                                className="sidebar-action-btn-modern text-decoration-none"
                                                                onClick={onClose}
                                                            >
                                                                <i className="fab fa-whatsapp icon text-success ml-2"></i>
                                                                <span>{t("Pages.UsersControl.communication.whatsapp") || "واتساب"}</span>
                                                            </Link>
                                                        )}

                                                        {hasPhone && (
                                                            <Link 
                                                                to={`/ControlPanel/SMS/${phoneWithCode}`}
                                                                className="sidebar-action-btn-modern text-decoration-none"
                                                                onClick={onClose}
                                                            >
                                                                <i className="fal fa-comment-alt-lines icon text-info ml-2"></i>
                                                                <span>{t("Pages.UsersControl.communication.sms") || "رسالة SMS"}</span>
                                                            </Link>
                                                        )}

                                                        <button type="button" className="sidebar-action-btn-modern d-flex align-items-center justify-content-between" onClick={() => handleActionClick("notes")}>
                                                            <span className="d-flex align-items-center">
                                                                <i className={`far fa-sticky-note icon ml-2 ${activeUser.notesCount > 0 ? "text-danger" : "text-primary"}`}></i>
                                                                <span>{t("Pages.UsersControl.action.notes")}</span>
                                                            </span>
                                                            {activeUser.notesCount > 0 && (
                                                                <span className="badge badge-danger px-2 py-1" style={{ borderRadius: '10px', fontSize: '0.75rem' }}>
                                                                    {activeUser.notesCount}
                                                                </span>
                                                            )}
                                                        </button>

                                                        {userTypeId !== 7 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("change_password")}>
                                                                <i className="far fa-key icon text-secondary ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.change_password")}</span>
                                                            </button>
                                                        )}

                                                        {userTypeId !== 1 && userTypeId !== 7 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeLoginInfo")}>
                                                                <i className="far fa-fingerprint icon text-success ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.changeLoginInfo")}</span>
                                                            </button>
                                                        )}

                                                        {userTypeId === 5 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("move")}>
                                                                <i className="far fa-exchange icon text-warning ml-2"></i>
                                                                <span>{t("Pages.StudentInClassRoomsControl.action.move")}</span>
                                                            </button>
                                                        )}

                                                        {(userTypeId === 2 || userTypeId === 7) && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("bills")}>
                                                                <i className="far fa-file-invoice-dollar icon text-info ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.bills")}</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Reports & Logs Group */}
                                                    <div className="sidebar-group-modern">
                                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.export.category.activity") || "السجلات والتقارير"}</div>

                                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("lessonMarks")}>
                                                            <i className="far fa-graduation-cap icon text-warning ml-2"></i>
                                                            <span>{t("Pages.UsersControl.action.lessonMarks") || "علامات الدروس والمهام"}</span>
                                                        </button>

                                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("lessonViews")}>
                                                            <i className="far fa-book-open icon text-primary ml-2"></i>
                                                            <span>{t("Pages.UsersControl.action.lessonViews") || "سجل مشاهدة الدروس"}</span>
                                                        </button>

                                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("downloadFiles")}>
                                                            <i className="far fa-download icon text-success ml-2"></i>
                                                            <span>{t("Pages.UsersControl.action.downloadFiles") || "الملفات المحملة"}</span>
                                                        </button>

                                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("errorReports")}>
                                                            <i className="far fa-bug icon text-danger ml-2"></i>
                                                            <span>{t("Pages.UsersControl.action.errorReports") || "بلاغات الأخطاء"}</span>
                                                        </button>

                                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("contactMessages")}>
                                                            <i className="far fa-envelope icon text-info ml-2"></i>
                                                            <span>{t("Pages.UsersControl.action.contactMessages") || "رسائل اتصل بنا"}</span>
                                                        </button>
                                                    </div>

                                                    {/* Permissions & Operations */}
                                                    <div className="sidebar-group-modern">
                                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.export.category.account") || "الصلاحيات والإدارة"}</div>

                                                        {userTypeId === 2 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("testUser")}>
                                                                <i className="fad fa-alarm-exclamation icon text-danger ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.testUser")}</span>
                                                            </button>
                                                        )}

                                                        {userTypeId === 3 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changePermission")}>
                                                                <i className="fad fa-water-lower icon text-danger ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.makeSchoolTeacher")}</span>
                                                            </button>
                                                        )}

                                                        {(userTypeId === 3 || userTypeId === 4) && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("accessToAllClass")}>
                                                                <i className="fa-solid fa-users-between-lines icon text-primary ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.accessToAllClass")}</span>
                                                            </button>
                                                        )}

                                                        {userTypeId === 4 && (
                                                            <>
                                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changePermission")}>
                                                                    <i className="fad fa-water-rise icon text-danger ml-2"></i>
                                                                    <span>{t("Pages.UsersControl.action.makeSchoolManager")}</span>
                                                                </button>
                                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("showChangeSchool")}>
                                                                    <i className="far fa-school icon text-primary ml-2"></i>
                                                                    <span>{t("Pages.UsersControl.action.changeSchool")}</span>
                                                                </button>
                                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeUserType")}>
                                                                    <i className="far fa-user-tag icon text-warning ml-2"></i>
                                                                    <span>{t("Pages.UsersControl.action.changeTypeToUser")}</span>
                                                                </button>
                                                            </>
                                                        )}

                                                        {userTypeId === 2 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeUserType")}>
                                                                <i className="far fa-chalkboard-teacher icon text-warning ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.changeTypeToSchoolTeacher")}</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Advanced & Destructive */}
                                                    <div className="sidebar-group-modern">
                                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.actionsMenu") || "خيارات متقدمة"}</div>
                                                        
                                                        {userTypeId !== 7 && (
                                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("useAs")}>
                                                                <i className="fad fa-alien icon text-warning ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.useAs") + " " + activeUser.Name.split(" ")[0]}</span>
                                                            </button>
                                                        )}

                                                        {userTypeId !== 7 && (
                                                            <button type="button" className="sidebar-action-btn-modern danger-zone-btn mt-2" onClick={() => handleActionClick("remove")}>
                                                                <i className="far fa-trash icon text-danger ml-2"></i>
                                                                <span>{t("Pages.UsersControl.action.remove")}</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                        <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                            {t("Close") || "إغلاق"}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Error Reports Modal */}
            {activeModal === "errorReports" && (
                <UserErrorReportsModal
                    show={true}
                    onClose={onClose}
                    user={activeUser}
                    t={t}
                />
            )}

            {/* Contact Messages Modal */}
            {activeModal === "contactMessages" && (
                <UserContactMessagesModal
                    show={true}
                    onClose={onClose}
                    user={activeUser}
                    t={t}
                />
            )}

            {/* Download Files Modal */}
            {activeModal === "downloadFiles" && (
                <UserFileHistoryModal
                    show={true}
                    onClose={onClose}
                    user={activeUser}
                    t={t}
                />
            )}

            {/* Lesson Marks Modal */}
            {activeModal === "lessonMarks" && (
                <UserLessonMarksModal
                    show={true}
                    onClose={onClose}
                    user={activeUser}
                    t={t}
                />
            )}

            {/* Lesson Views Modal */}
            {activeModal === "lessonViews" && (
                <UserLessonHistoryModal
                    show={true}
                    onClose={onClose}
                    user={activeUser}
                    t={t}
                />
            )}
        </>
    );
};

const UserFileHistoryModal = ({ show, onClose, user, t }) => {
    const [historyList, setHistoryList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!show || !user) return;
        setLoading(true);
        api.get(`/UserFileHistory?userId=${user.Id}&count=100`)
            .then(resp => {
                setHistoryList(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [show, user]);

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-success">
                            <i className="fad fa-download"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">
                                {t("Pages.UsersControl.action.downloadFiles") || "الملفات المحملة"}
                            </div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : historyList.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom">
                        {historyList.map((item) => (
                            <div key={item.Id} className="history-item-card border-left-success">
                                <div className="history-item-header d-flex justify-content-between align-items-start">
                                    <div className="history-item-meta text-right">
                                        <span className="badge badge-light p-2 mb-1" style={{ fontSize: '0.85rem' }}>
                                            <i className="fal fa-file-alt ml-1 text-primary"></i>
                                            {item.FileName}
                                        </span>
                                    </div>
                                    <div>
                                        <span className={`badge ${item.IsPublic ? "badge-info" : "badge-secondary"} p-2`}>
                                            {item.IsPublic ? (t("Pages.EducationalFiles.Public") || "عام") : (t("Pages.EducationalFiles.Private") || "خاص")}
                                        </span>
                                    </div>
                                </div>
                                <div className="history-item-footer mt-3 d-flex justify-content-end text-muted small">
                                    <span>
                                        <i className="fal fa-calendar-alt ml-1"></i>
                                        {item.CreationDate}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const getTodayDateString = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
};

const UserLessonMarksModal = ({ show, onClose, user, t }) => {
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!show || !user) return;
        setLoading(true);
        const today = getTodayDateString();
        api.get(`/UserLessonMark?userId=${user.Id}&startDate=01/01/2000&endDate=${today}&count=100&groupLesson=true`)
            .then(resp => {
                setLessons(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [show, user]);

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-warning">
                            <i className="fad fa-graduation-cap"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">
                                {t("Pages.UsersControl.action.lessonMarks") || "علامات الدروس والمهام"}
                            </div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : lessons.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom">
                        {lessons.map((lesson, idx) => (
                            <div key={lesson.Id || idx} className="history-item-card border-left-warning">
                                <div className="history-item-header d-flex justify-content-between align-items-start">
                                    <div className="history-item-meta text-right">
                                        <h6 className="font-weight-bold text-dark mb-1">
                                            <i className="fal fa-book-open ml-2 text-primary"></i>
                                            {lesson.Name}
                                        </h6>
                                        {lesson.Index && (
                                            <span className="small text-muted d-block mt-1">
                                                <i className="fal fa-folder ml-1"></i>
                                                {lesson.Index.Name}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-left">
                                        <span className="badge badge-warning p-2" style={{ fontSize: '0.85rem' }}>
                                            {t("AttemptsCount") || "عدد المحاولات"}: {lesson.Attempts?.length || 0}
                                        </span>
                                    </div>
                                </div>
                                <div className="history-item-body mt-2">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-borderless mb-0 small text-right w-100" style={{ direction: 'rtl' }}>
                                            <thead>
                                                <tr className="border-bottom text-muted">
                                                    <th className="text-right">{t("AttemptsCount") || "المحاولة"}</th>
                                                    <th className="text-right">{t("Pages.UsersControl.advancedSearch.IsActive") || "العلامة"}</th>
                                                    <th className="text-right">{t("Date") || "التاريخ"}</th>
                                                    <th className="text-right">{t("Time") || "المدة الزمنية"}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lesson.Attempts?.map((attempt, aIdx) => {
                                                    const durationText = attempt.UserTime?.duration ? `${attempt.UserTime.duration} ${t("Second") || "ثانية"}` : "-";
                                                    const markPercent = attempt.TotalMark ? (attempt.Mark / attempt.TotalMark) * 100 : 0;
                                                    let markBadgeClass = "badge-danger";
                                                    if (markPercent >= 80) markBadgeClass = "badge-success";
                                                    else if (markPercent >= 50) markBadgeClass = "badge-warning";

                                                    return (
                                                        <tr key={attempt.Id || aIdx}>
                                                            <td>#{lesson.Attempts.length - aIdx}</td>
                                                            <td>
                                                                <span className={`badge ${markBadgeClass} px-2 py-1`}>
                                                                    {attempt.Mark} / {attempt.TotalMark}
                                                                </span>
                                                            </td>
                                                            <td>{attempt.CreationDate}</td>
                                                            <td>{durationText}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const UserLessonHistoryModal = ({ show, onClose, user, t }) => {
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!show || !user) return;
        setLoading(true);
        const today = getTodayDateString();
        api.get(`/UserLessonHistory?userId=${user.Id}&startDate=01/01/2000&endDate=${today}&count=100&groupLesson=true`)
            .then(resp => {
                setLessons(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [show, user]);

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-primary">
                            <i className="fad fa-book-open"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">
                                {t("Pages.UsersControl.action.lessonViews") || "سجل مشاهدة الدروس"}
                            </div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : lessons.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom">
                        {lessons.map((lesson, idx) => (
                            <div key={lesson.Id || idx} className="history-item-card border-left-primary">
                                <div className="history-item-header d-flex justify-content-between align-items-start">
                                    <div className="history-item-meta text-right">
                                        <h6 className="font-weight-bold text-dark mb-1">
                                            <i className="fal fa-book-open ml-2 text-primary"></i>
                                            {lesson.Name}
                                        </h6>
                                        {lesson.Index && (
                                            <span className="small text-muted d-block mt-1">
                                                <i className="fal fa-folder ml-1"></i>
                                                {lesson.Index.Name}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex flex-column align-items-end gap-1">
                                        <span className="badge badge-info p-2" style={{ fontSize: '0.85rem' }}>
                                            {t("ViewsCount") || "عدد المشاهدات"}: {lesson.Attempts?.length || 0}
                                        </span>
                                        {lesson.BestMark !== null && lesson.BestMark !== undefined && (
                                            <span className="badge badge-success p-2 mt-1" style={{ fontSize: '0.85rem' }}>
                                                {t("BestMark") || "أفضل علامة"}: {lesson.BestMark} / {lesson.TotalMark}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="history-item-body mt-2">
                                    <div className="small text-muted font-weight-bold mb-1">{t("ViewsTimeline") || "تواريخ المشاهدة"}:</div>
                                    <div className="d-flex flex-wrap gap-2 mt-1 justify-content-start" style={{ direction: 'rtl' }}>
                                        {lesson.Attempts?.map((attempt, aIdx) => (
                                            <span key={attempt.Id || aIdx} className="badge badge-light p-2">
                                                <i className="fal fa-eye ml-1 text-info"></i>
                                                {attempt.CreationDate}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const UserErrorReportsModal = ({ show, onClose, user, t }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!show || !user) return;
        setLoading(true);
        api.get(`/ErrorReport/?userId=${user.Id}&count=100`)
            .then(resp => {
                setReports(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [show, user]);

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-danger">
                            <i className="fad fa-bug"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.action.errorReports") || "بلاغات الأخطاء"}</div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : reports.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom">
                        {reports.map((report) => (
                            <div key={report.Id} className="history-item-card border-left-danger">
                                <div className="history-item-header d-flex justify-content-between align-items-start">
                                    <div className="history-item-meta">
                                        <span className="badge badge-light p-2 mb-1" style={{ fontSize: '0.8rem' }}>
                                            <i className="fal fa-book-open ml-1 text-primary"></i>
                                            {t("Pages.Lessons.Lesson") || "الدرس"}: {report.Name}
                                        </span>
                                        {report.Question && (
                                            <span className="badge badge-light p-2 mb-1 mr-2" style={{ fontSize: '0.8rem' }}>
                                                <i className="fal fa-question-circle ml-1 text-info"></i>
                                                {t("Pages.QuestionsControl.title") || "السؤال"}: {report.Question.Name}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className={`badge ${report.IsSolved ? "badge-success" : "badge-danger"} p-2`}>
                                            <i className={`fas ${report.IsSolved ? "fa-check" : "fa-exclamation-circle"} ml-1`}></i>
                                            {report.IsSolved ? (t("Pages.ErrorReport.solved") || "تم الحل") : (t("Pages.ErrorReport.active") || "نشط")}
                                        </span>
                                        {report.IsArchived && (
                                            <span className="badge badge-secondary p-2">
                                                <i className="fas fa-archive ml-1"></i>
                                                {t("Pages.ErrorReport.archive") || "مؤرشف"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="history-item-body mt-2">
                                    <div className="small text-muted font-weight-bold mb-1">{t("Pages.ErrorReport.Note.title") || "الملاحظة"}:</div>
                                    <p className="history-item-text">{report.Note || t("NoInfo")}</p>
                                </div>
                                <div className="history-item-footer mt-2 d-flex justify-content-end text-muted small">
                                    <span>
                                        <i className="fal fa-calendar-alt ml-1"></i>
                                        {report.CreationDate}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const UserContactMessagesModal = ({ show, onClose, user, t }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!show || !user) return;
        setLoading(true);
        api.get(`/ContactUs/?userId=${user.Id}`)
            .then(resp => {
                setMessages(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [show, user]);

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-info">
                            <i className="fad fa-envelope"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.action.contactMessages") || "رسائل اتصل بنا"}</div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom">
                        {messages.map((msg) => (
                            <div key={msg.Id} className="history-item-card border-left-info">
                                <div className="history-item-header d-flex justify-content-between align-items-start">
                                    <div className="history-item-meta">
                                        <span className="badge badge-light p-2 mb-1" style={{ fontSize: '0.8rem' }}>
                                            <i className="fal fa-heading ml-1 text-primary"></i>
                                            {t("Title") || "العنوان"}: {msg.Title}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className={`badge ${msg.IsRead ? "badge-success" : "badge-warning"} p-2`}>
                                            <i className={`fas ${msg.IsRead ? "fa-envelope-open" : "fa-envelope"} ml-1`}></i>
                                            {msg.IsRead ? (t("Read") || "مقروءة") : (t("Unread") || "غير مقروءة")}
                                        </span>
                                        {msg.RepliesCount > 0 && (
                                            <span className="badge badge-info p-2">
                                                <i className="fas fa-reply ml-1"></i>
                                                {t("Replies") || "الردود"}: {msg.RepliesCount}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="history-item-body mt-2">
                                    <div className="small text-muted font-weight-bold mb-1">{t("Details") || "التفاصيل"}:</div>
                                    <p className="history-item-text">{msg.Details || t("NoInfo")}</p>
                                </div>
                                <div className="history-item-footer mt-2 d-flex justify-content-between align-items-center text-muted small">
                                    <div className="d-flex gap-2">
                                        {msg.Sender && (
                                            <span className="mr-2">
                                                <i className="fal fa-user ml-1"></i>
                                                {t("Sender") || "المرسل"}: {msg.Sender}
                                            </span>
                                        )}
                                        {msg.Email && (
                                            <span>
                                                <i className="fal fa-envelope ml-1"></i>
                                                {msg.Email}
                                            </span>
                                        )}
                                    </div>
                                    <span>
                                        <i className="fal fa-calendar-alt ml-1"></i>
                                        {msg.CreationDate}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserModals;
